/**
 * TodoSync.ts
 *
 * Todo 동기화를 담당하는 모듈
 *
 * 주요 기능:
 * - Todo 폴더 목록 동기화 (fetchTodoFolders)
 * - Todo 항목 데이터 동기화 (fetchTodos)
 * - 동기화 큐 관리 및 최적화
 * - 폴더별 동기화 설정 관리
 * - Todo 데이터 파싱 및 처리
 * - UI 업데이트 이벤트 관리
 */

import { EventEmitter } from 'events'
import { DAVNamespaceShort } from 'libs/tsdav'

import { getClient } from 'pages/calendar/modules/CalendarClient' // 기존 클라이언트 재사용

import { TodoFolder } from './TodoFolder'
import * as todoFolderService from './TodoFolderSync'
import { TodoItem, syncState } from './TodoItem'
import * as todoService from './TodoService'
import { parseVTodos } from './VTodoParser'
import { initializeTodoDBSystem, syncTodosToDB } from './todoDB/TodoDBService'
import { fetchNotifications } from 'pages/calendar/modules/NotificationService'
import settingSyncService from 'services/settingSync'

// const DEBUG = false

// Todo 전용 상태 관리 (CalendarState와 유사)
let isFetchingTodoFolders = false

// Todo 동기화 큐: 중복 동기화 요청을 방지하고 순차 처리를 위한 큐
// - key: 폴더 URL (중복 확인용)
// - folder: 동기화할 TodoFolder 객체
let fetchingTodoQueue: Array<{ key: string; folder: TodoFolder }> = []

// Event emitters for UI updates (similar to calendar module)
export const todoFolderEmitter = new EventEmitter()
export const todoItemEmitter = new EventEmitter()
export const todoSyncEmitter = new EventEmitter()

// UI 업데이트 이벤트 리스너 등록 함수들
export const addTodoFolderListener = (listener: () => void) => {
  todoFolderEmitter.on('folder', listener)
}

export const addTodoItemListener = (listener: () => void) => {
  todoItemEmitter.on('item', listener)
}

export const addTodoSyncListener = (listener: (isSyncing: boolean) => void) => {
  todoSyncEmitter.on('sync', listener)
}

// UI 업데이트 이벤트 리스너 제거 함수들
export const removeTodoFolderListener = (listener: () => void) => {
  todoFolderEmitter.off('folder', listener)
}

export const removeTodoItemListener = (listener: () => void) => {
  todoItemEmitter.off('item', listener)
}

export const removeTodoSyncListener = (listener: (isSyncing: boolean) => void) => {
  todoSyncEmitter.off('sync', listener)
}

// 동기화 상태 이벤트 발생 함수 -> sync icon rotate 처리
const _emitSyncStatus = (isSyncing: boolean = false) => {
  todoSyncEmitter.emit('sync', isSyncing)
}

// 동기화 상태 확인 (폴더 조회 또는 Todo 동기화 진행 중인지)
export const isSyncing = () => {
  return isFetchingTodoFolders || fetchingTodoQueue.length > 0
}

// Todo 폴더 동기화
export const fetchTodoFolders = async (
  isInit = false,
  completeCallback = (_success: boolean) => {},
) => {
  if (isFetchingTodoFolders) {
    return
  }
  isFetchingTodoFolders = true

  // 동기화 시작 알림
  _emitSyncStatus(true)

  const client = getClient()
  try {
    const response = await client.fetchCalendars({
      props: {
        [`${DAVNamespaceShort.CALDAV}:calendar-description`]: {},
        [`${DAVNamespaceShort.CALDAV}:calendar-timezone`]: {},
        [`${DAVNamespaceShort.DAV}:displayname`]: {},
        [`${DAVNamespaceShort.CALDAV_APPLE}:calendar-color`]: {},
        [`${DAVNamespaceShort.CALENDAR_SERVER}:getctag`]: {},
        [`${DAVNamespaceShort.DAV}:resourcetype`]: {},
        [`${DAVNamespaceShort.CALDAV}:supported-calendar-component-set`]: {},
        [`${DAVNamespaceShort.DAV}:sync-token`]: {},
        [`${DAVNamespaceShort.CALDAV_APPLE}:calendar-order`]: {},
        [`${DAVNamespaceShort.DAV}:creationdate`]: {},
        [`${DAVNamespaceShort.DAV}:current-user-privilege-set`]: {},
      },
      projectedProps: {
        calendarOrder: true,
        creationdate: true,
        currentUserPrivilegeSet: true,
      },
    })

    // VTODO만 필터링
    const todoFolders = response.filter((calendar) => {
      return !calendar.components?.includes('VEVENT') && calendar.components?.includes('VTODO')
    })

    // TodoService에 폴더 정보 전달하고 변경사항이 있는지 확인
    const userInfo = todoFolderService.getUserInfo()
    const hasChanges = await todoFolderService.setFolders(userInfo, todoFolders as any, isInit)

    // 변경사항이 있을 때만 UI 업데이트 트리거
    if (hasChanges || isInit) {
      todoFolderEmitter.emit('folder')
      console.log('Todo folders fetched and UI updated')
    } else {
      console.log('Todo folders fetched but no changes detected')
    }
    isFetchingTodoFolders = false
    completeCallback(true)
  } catch (error) {
    console.log('fetch todo folders failed:', error)
    completeCallback(false)
  } finally {
    isFetchingTodoFolders = false
    // 동기화 완료 알림
    _emitSyncStatus(false)
  }
}

// 동기화 활성화/비활성화
export const enableSync = (folderUrl: string, isSync: boolean) => {
  console.log('[enableSync] Starting folder sync toggle:', folderUrl, isSync)
  const folders = todoFolderService.getFolders()
  const folder = folders.find((f) => f.url === folderUrl)

  if (!folder) {
    console.warn('[enableSync] Folder not found:', folderUrl)
    return
  }

  if (folder.isSync === isSync) {
    console.log('[enableSync] Sync state already matches:', folderUrl, isSync)
    // Still emit UI update events even if state matches to refresh the UI
    todoFolderEmitter.emit('folder')
    todoItemEmitter.emit('item')
    console.log('[enableSync] Events emitted for UI refresh (state already matched)')
    return
  }

  // updateFolderSync에서 상태 변경을 처리하므로 여기서는 직접 수정하지 않음
  const handleSyncUpdate = async () => {
    try {
      await todoFolderService.updateFolderSync(folderUrl, isSync)
      console.log('[enableSync] updateFolderSync completed')

      if (isSync) {
        // Todo 항목 동기화 시작 (큐 시스템 사용하여 중복 요청 방지)
        const canFetch = _addFetchQueue(folder)
        if (canFetch) {
          console.log('[enableSync] Starting fetch queue for folder:', folderUrl)
          // 큐에 성공적으로 추가된 경우에만 동기화 실행
          _fetchTodos(folder, async () => {
            _completeFetchQueue(folder.url) // 완료 후 큐에서 제거 및 다음 항목 처리
            // 폴더 상태 변경 UI 업데이트 트리거
            console.log('[enableSync] Emitting folder event after fetch completion : ' + folderUrl)
            todoFolderEmitter.emit('folder')
          })
        } else {
          // canFetch가 false인 경우는 이미 동기화 중이므로 별도 처리 불필요
          console.log('[enableSync] Fetch already in progress for folder:', folderUrl)
        }
      } else {
        // Todo 항목 제거
        console.log('[enableSync] Removing todo items for folder:', folderUrl)
        await todoService.removeTodoItems(folder.url)
        console.log(`[enableSync] 동기화 비활성화 및 데이터 삭제 완료: ${folderUrl}`)
      }

      // UI 업데이트 트리거 (동기화 해제 시에도 UI 업데이트)
      todoFolderEmitter.emit('folder')
      todoItemEmitter.emit('item')
      console.log('[enableSync] Events emitted - folder and item')
    } catch (error) {
      console.error('[enableSync] Error during sync update:', error)
      // 에러가 발생해도 UI 업데이트는 시도
      todoFolderEmitter.emit('folder')
      todoItemEmitter.emit('item')
    }
  }

  // async 작업 실행
  handleSyncUpdate()
}

// 전체 Todo 동기화
export const reloadTodos = (fullReload = false, skipSync: boolean = false) => {
  // (todo, calendar)는 setting의 변경 사항이 UI 업데이트에 바로 반영되어야 하는 경우가 많으므로, 강제 동기화 수행
  if (skipSync === false) {
    settingSyncService.sync(fullReload).catch(() => {})
  }

  // TodoDB 시스템 초기화 (최초 1회)
  initializeTodoDBSystem().catch((error) => {
    console.error('[TodoSync] Failed to initialize TodoDB system:', error)
  })

  if (fullReload) {
    fetchTodoFolders(skipSync === false, (success) => {
      console.log('[TodoSync] Todo folders reloaded:', success ? 'success' : 'failed')
      // fetchTodoFolders 내부에서 이미 UI 업데이트가 트리거됨
    })
  }

  fetchNotifications()

  const folders = todoFolderService.getFolders()
  let completedCount = 0
  const totalFolders = folders.filter((f) => f.isSync).length

  if (totalFolders === 0) {
    // 동기화할 폴더가 없어도 UI 업데이트 트리거
    todoItemEmitter.emit('item')
    return
  }

  // 각 폴더별로 큐 시스템을 통해 순차 동기화
  folders.forEach((folder) => {
    if (folder.isSync) {
      const canFetch = _addFetchQueue(folder)
      if (canFetch) {
        _fetchTodos(folder, async () => {
          _completeFetchQueue(folder.url)
          completedCount++
          console.log(
            `[TodoSync] 폴더 동기화 완료: ${folder.displayName} (${completedCount}/${totalFolders})`,
          )

          // 모든 폴더 동기화 완료 시 로그
          if (completedCount === totalFolders) {
            console.log(`[TodoSync] 전체 ${totalFolders}개 폴더 동기화 완료`)
          }
        })
      }
    }
  })
}

/**
 * 특정 폴더만 동기화하고 완료 시 콜백 호출
 * @param folderUrl 동기화할 폴더 URL
 * @param completeCallback 동기화 완료 후 호출할 콜백 (업데이트된 todos 배열 전달)
 */
export const reloadTodosForFolder = (
  folderUrl: string,
  completeCallback = (_success: boolean, _updatedTodos?: TodoItem[]) => {},
) => {
  const folders = todoFolderService.getFolders()
  const folder = folders.find((f) => f.url === folderUrl)

  if (!folder) {
    console.error('[reloadTodosForFolder] Folder not found:', folderUrl)
    completeCallback(false)
    return
  }

  if (!folder.isSync) {
    console.warn('[reloadTodosForFolder] Folder sync is disabled:', folderUrl)
    completeCallback(false)
    return
  }

  // 큐 시스템을 사용하여 중복 동기화 방지
  const canFetch = _addFetchQueue(folder)
  if (!canFetch) {
    console.log('[reloadTodosForFolder] Folder already syncing:', folderUrl)
    // 이미 진행중이라도 성공으로 처리 (UI는 emitter로 업데이트됨)
    completeCallback(true)
    return
  }

  // 폴더 동기화 실행
  _fetchTodos(folder, async () => {
    _completeFetchQueue(folder.url)

    // 동기화 완료 후 최신 데이터 가져오기
    try {
      const updatedTodos = await todoService.getTodoItems(folderUrl)
      console.log(
        `[reloadTodosForFolder] Sync completed for ${folder.displayName}:`,
        updatedTodos.length,
        'items',
      )
      completeCallback(true, updatedTodos)
    } catch (error) {
      console.error('[reloadTodosForFolder] Failed to get updated todos:', error)
      completeCallback(false)
    }
  })
}

/**
 * Todo 동기화 큐에 폴더 추가
 * - 동일한 폴더의 중복 동기화 요청을 방지
 * - 큐가 비어있었다면 동기화 시작 이벤트 발생
 *
 * @param folder 동기화할 TodoFolder 객체
 * @returns 큐에 추가 성공 여부 (true: 새로 추가됨, false: 이미 처리 중)
 */
const _addFetchQueue = (folder: TodoFolder): boolean => {
  const key = folder.url
  const isContains = fetchingTodoQueue.some((item) => item.key === key)

  if (isContains) {
    console.log('[TodoSync]이미 동기화 중인 폴더:', key)
    return false
  }

  // 큐에 새 폴더 추가
  fetchingTodoQueue.push({ key, folder })

  // 큐가 비어있었다가 첫 항목이 추가된 경우 동기화 시작 신호
  if (fetchingTodoQueue.length === 1) {
    _emitSyncStatus(true)
  }
  return true
}

/**
 * Todo 동기화 큐에서 완료된 항목 제거 및 다음 항목 처리
 * - 완료된 폴더를 큐에서 제거
 * - 큐에 대기 중인 다음 폴더가 있으면 자동으로 동기화 시작
 * - 큐가 모두 비워지면 동기화 완료 이벤트 발생
 *
 * @param key 완료된 폴더의 URL
 */
const _completeFetchQueue = (key: string) => {
  // 완료된 항목을 큐에서 제거
  fetchingTodoQueue = fetchingTodoQueue.filter((item) => item.key !== key)

  // 큐에 대기 중인 항목이 있으면 다음 폴더 동기화 시작
  if (fetchingTodoQueue.length > 0) {
    const target = fetchingTodoQueue[0]
    _fetchTodos(target.folder, async () => {
      // 재귀 호출로 큐 항목들을 순차적으로 처리
      _completeFetchQueue(target.key)
    })
  } else {
    // 큐가 모두 비워지면 동기화 완료 신호
    _emitSyncStatus(false)
  }
}

/**
 * Todo 항목 동기화 (etag 기반 변경 감지 포함)
 * - etag 기반 변경 감지: 서버 etag ≠ 로컬 etag인 경우만 파싱/업데이트
 * - 성능 최적화: 변경되지 않은 항목은 기존 데이터 유지
 * - syncTodosToDB() 호출하여 DB 동기화
 *
 *  *  Sync 후 사용자가 보는 정보 업데이트 흐름:
 *   1. 서버 sync → etag 기반 변경 감지
 *   2. 메모리 캐시 업데이트 → 즉시 반영
 *   3. DB 업데이트 → 서버 데이터로 전체 교체
 *   4. todoItemEmitter.emit('item') → UI 컴포넌트에 알림
 *   5. useTodoList.loadTodos() → DB 또는 메모리 캐시에서 최신 데이터 조회
 *   6. setTodoList(filteredTodos) → 사용자 화면 갱신
 */
const _fetchTodos = async (folder: TodoFolder, completeCallback = async () => {}) => {
  if (!folder.isSync) {
    await completeCallback()
    return
  }

  const client = getClient()
  try {
    // 폴더 내 모든 VTODO 항목 가져오기 (시간 범위 없이 전체 조회 - VTODO spec)
    const todoArr = await client.fetchCalendarObjects({
      calendar: folder as any,
      filters: {
        'comp-filter': {
          _attributes: {
            name: 'VCALENDAR',
          },
          'comp-filter': {
            _attributes: {
              name: 'VTODO',
            },
          },
        },
      },
    })

    console.log(`[TodoSync] ${folder.displayName}에서 ${todoArr.length}개 Todo 조회됨`)

    // 기존 Todo 항목들 가져오기 (etag 비교용)
    const existingTodos = await todoService.getTodoItems(folder.url)
    const existingTodoDict: Record<string, TodoItem> = {}
    existingTodos.forEach((todo) => {
      existingTodoDict[todo.url] = todo
    })

    // 새로운 Todo 딕셔너리 생성
    const newTodoDict: Record<string, TodoItem> = {}
    let hasChanges = false

    /**
     * ===== ETAG 기반 변경 감지 및 동기화 처리 =====
     *
     * ETag (Entity Tag)는 HTTP 리소스의 버전 식별자로:
     * - 서버에서 리소스 내용이 변경될 때마다 새로운 etag 값 생성
     * - 클라이언트는 etag를 비교하여 변경 여부를 효율적으로 감지
     * - 변경되지 않은 리소스는 다시 파싱하지 않아 성능 최적화
     *
     * 동기화 로직 - CalDav 프로토콜 기반
     * 1. 서버 etag와 로컬 etag 비교
     * 2. 동일하면 → 변경 없음, 기존 데이터 유지 (파싱 스킵)
     * 3. 다르면 → 변경됨, ICS 파싱 후 업데이트
     * 4. 서버에 없으면 → 삭제됨, 로컬에서 제거
     */
    todoArr.forEach((todoData) => {
      const existingTodo = existingTodoDict[todoData.url]

      // ===== CASE 1: 변경 없음 (etag 동일) =====
      // 서버와 로컬의 etag가 같으면 내용이 변경되지 않았음
      // → 기존 데이터를 그대로 유지하여 불필요한 ICS 파싱 방지
      if (existingTodo && existingTodo.etag === todoData.etag) {
        // 로컬 수정이 없으면 SERVER 상태로 보정
        const todoToKeep = {
          ...existingTodo,
          syncState: syncState.SERVER,
        }
        newTodoDict[todoData.url] = todoToKeep
        delete existingTodoDict[todoData.url] // 삭제되지 않은 항목으로 표시
        return
      }

      // ===== CASE 2: 변경됨 또는 새 항목 (etag 다름 또는 없음) =====
      // etag가 다르거나 새 항목인 경우에만 ICS 파싱 수행 (성능 최적화)
      // → 서버에서 받은 최신 ICS 데이터를 파싱하여 TodoItem 객체로 변환
      const todoDict = parseVTodos([todoData as any], folder)
      if (todoDict[todoData.url]) {
        // 서버에서 받은 원본 데이터(url, etag 등)와 파싱된 데이터를 병합
        const serverTodo = {
          ...todoDict[todoData.url],
          url: todoData.url, // 서버 원본 URL 사용
          etag: todoData.etag, // 서버 원본 etag 사용
          syncState: syncState.SERVER,
        }
        newTodoDict[todoData.url] = serverTodo
        hasChanges = true
        console.log(`[TodoSync] ${existingTodo ? '업데이트' : '새 항목'}:`, todoData.url)
      }
    })

    // ===== CASE 3: 삭제된 항목 확인 =====
    // existingTodoDict에 남아있는 항목들은 서버에서 더 이상 존재하지 않는 항목들
    // → 로컬에서도 삭제해야 함
    const deletedTodos = Object.keys(existingTodoDict)
    if (deletedTodos.length > 0) {
      hasChanges = true
      console.log(`[TodoSync] 삭제된 항목 ${deletedTodos.length}개:`, deletedTodos)
    }

    /**
     * ===== 변경사항이 있을 때만 업데이트 수행 =====
     *
     * 1. 메모리 캐시 업데이트 (즉시 반영)
     * 2. DB 동기화 (서버 데이터로 교체)
     * 3. UI 업데이트 트리거 (사용자 화면 갱신)
     */

    // 1. TodoService 메모리 캐시 업데이트
    if (hasChanges) {
      await todoService.updateTodoItems(folder.url, Object.values(newTodoDict))
    }

    // 2. DB에도 동기화 (변경사항이 있을 때만)
    if (hasChanges) {
      try {
        await syncTodosToDB(folder.url, Object.values(newTodoDict))
        console.log(`[TodoSync] DB 동기화 완료: ${folder.url}`)
      } catch (error) {
        console.error('[TodoSync] DB 동기화 실패:', error)
      }
    }

    // 3. 변경사항이 있을 때만 UI 업데이트 트리거
    // → useTodoList.loadTodos() 호출하여 사용자 화면 갱신
    if (hasChanges) {
      todoItemEmitter.emit('item')
      console.log(`[TodoSync] UI 업데이트 트리거됨: ${folder.displayName}`)
    } else {
      console.log(`[TodoSync] 변경사항 없음: ${folder.displayName}`)
    }

    await completeCallback()
  } catch (error) {
    console.error(`[TodoSync] ${folder.displayName} 동기화 실패:`, error)
    await completeCallback()
  }
}
